let generation = 0;
class TopbrushReviews extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.querySelector('[data-reviews-load]').addEventListener('click', () => this.load(), { signal: this.abort.signal });
    if (!this.loaded && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.load();
      }, { rootMargin: '200px' });
      this.observer.observe(this);
    }
  }
  disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); }
  async load() {
    if (this.pending || this.loaded) return;
    this.pending = true;
    this.observer?.disconnect();
    const button = this.querySelector('[data-reviews-load]');
    const status = this.querySelector('[data-reviews-status]');
    button.disabled = true;
    this.setAttribute('aria-busy', 'true');
    let step = 'data';
    delete this.dataset.errorStep;
    try {
      const { parseWidgetData, collectCss, reviewMountReady, reviewEntryUrl, loadReviewCore } = await import(this.dataset.logicUrl);
      const fragment = this.querySelector('[data-reviews-template]').content.cloneNode(true);
      const widget = fragment.querySelector('.jdgm-review-widget[data-entry-point]');
      const dataScript = fragment.querySelector('script.jdgm-review-widget-data');
      if (!widget || !dataScript || widget.dataset.entryPoint !== 'review_widget.js') throw new Error('Review widget unavailable');
      const { productId, payload } = parseWidgetData(dataScript.textContent);
      const base = window.jdgm?.CDN_BASE_URL;
      if (!base || new URL(base).hostname !== 'cdn.shopify.com') throw new Error('Review loader unavailable');
      step = 'core';
      await loadReviewCore(window.jdgm);
      step = 'manifest';
      const response = await fetch(new URL('manifest.json', base), { signal: AbortSignal.any([this.abort.signal, AbortSignal.timeout(15000)]) });
      if (!response.ok) throw new Error('Review styles unavailable');
      const css = collectCss(await response.json(), widget.dataset.entryKey, base);
      step = 'styles';
      await Promise.all(css.map(href => new Promise((resolve, reject) => {
        if ([...document.querySelectorAll('link[rel="stylesheet"]')].some(link => link.href === href)) return resolve();
        const link = document.createElement('link');
        link.rel = 'stylesheet'; link.href = href;
        const timer = setTimeout(() => { link.remove(); reject(new Error('Review stylesheet timeout')); }, 15000);
        link.onload = () => { clearTimeout(timer); resolve(); };
        link.onerror = () => { clearTimeout(timer); link.remove(); reject(new Error('Review stylesheet unavailable')); };
        document.head.append(link);
      })));
      if (!this.isConnected) return;
      fragment.querySelectorAll('script').forEach(script => script.remove());
      window.jdgm.data ??= {};
      window.jdgm.data.reviewWidget ??= {};
      window.jdgm.data.reviewWidget[productId] = payload;
      const host = this.querySelector('[data-reviews-host]');
      delete host.dataset.fallback;
      host.replaceChildren(fragment);
      step = 'entry';
      await import(reviewEntryUrl(base, this.closest('[id]')?.id ?? 'reviews', ++generation));
      step = 'ready';
      await new Promise((resolve, reject) => {
        const ready = () => reviewMountReady(widget.hasAttribute('data-v-app'), widget.dataset.widgetName, widget.textContent);
        if (ready()) return resolve();
        const observer = new MutationObserver(() => { if (ready()) { clearTimeout(timer); observer.disconnect(); resolve(); } });
        const timer = setTimeout(() => { observer.disconnect(); reject(new Error('Review initialization unavailable')); }, 15000);
        observer.observe(widget, { attributes: true, childList: true, subtree: true, characterData: true });
      });
      if (!this.isConnected) return;
      this.loaded = true;
      button.hidden = true;
      status.textContent = '';
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.dataset.errorStep = step;
        status.textContent = status.dataset.error;
        const fallback = this.querySelector('[data-reviews-template]').content.cloneNode(true);
        fallback.querySelectorAll('script').forEach(script => script.remove());
        fallback.querySelectorAll('[data-entry-point]').forEach(root => { root.removeAttribute('data-entry-point'); root.removeAttribute('data-entry-key'); });
        const host = this.querySelector('[data-reviews-host]');
        host.dataset.fallback = 'true';
        host.replaceChildren(fallback);
      }
    } finally {
      this.pending = false;
      button.disabled = false;
      this.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('topbrush-reviews')) customElements.define('topbrush-reviews', TopbrushReviews);
